import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  Typography, Box, Button, TextField, ToggleButton, ToggleButtonGroup, 
  Card, CardContent, Avatar, Chip, IconButton, List, ListItem, Divider, 
  LinearProgress, Paper, Menu, MenuItem, ListItemIcon, ListItemText, 
  Dialog, DialogTitle, DialogContent, DialogActions, Backdrop, 
  CircularProgress, Alert, Snackbar, useTheme, Fade
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { 
  Image as ImageIcon, Chat as ChatIcon, Person as PersonIcon, 
  PhotoCamera, Brush, EmojiEmotions, ViewComfy, AutoAwesome, 
  Send, ContentCopy, MoreVert, Edit, Delete, PushPin, Share, 
  History, Fullscreen, FullscreenExit, Warning, Clear, Build
} from "@mui/icons-material";
import Close from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { aiSmartDesk, aiHumanizer } from "../../src/services/aiIntelligenzServices";

// Import access control hooks
import { useCheckFeatureAccess } from "../components/CheckFeature";
import { useCheckSubscriptionPlanAccess } from "../components/SubscriptionAccess";
import SubscriptionRequired from "../components/SubscriptionRequired";
import { checkAIRequestLimit } from "../components/aiRequestLimit";
import { addUserLogs } from "../../src/services/userLogServices";

const MAX_CHARACTERS = 1500;
const MAX_CHAT_WORDS = 1500; // For basic plan
const MAX_CHAT_WORDS_PREMIUM = 3500; // For premium plan

// Coming Soon Popup Component
const ComingSoonPopup = ({ open, onClose }) => {
  const theme = useTheme();
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
          backdropFilter: 'blur(10px)',
          border: `2px solid ${theme.palette.primary.main}30`,
          boxShadow: `0 20px 40px ${theme.palette.primary.main}20`,
          overflow: 'hidden',
          animation: 'pulse 2s ease-in-out',
          '@keyframes pulse': {
            '0%': { transform: 'scale(0.95)', opacity: 0 },
            '50%': { transform: 'scale(1.02)', opacity: 1 },
            '100%': { transform: 'scale(1)', opacity: 1 }
          }
        }
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 30% 50%, ${theme.palette.primary.main}20, transparent 50%),
                      radial-gradient(circle at 70% 50%, ${theme.palette.secondary.main}20, transparent 50%)`,
          zIndex: 0
        }}
      />
      
      <DialogTitle sx={{ position: 'relative', zIndex: 1, textAlign: 'center', pt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'rotate 10s linear infinite',
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              },
              boxShadow: `0 10px 30px ${theme.palette.primary.main}40`
            }}
          >
            <Build sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Coming Soon!
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ position: 'relative', zIndex: 1, textAlign: 'center', py: 4 }}>
        {/* Animated floating elements */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${20 + i * 15}%`,
              top: `${30 + i * 10}%`,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: i % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main,
              opacity: 0.3,
              animation: `float ${3 + i}s ease-in-out infinite`,
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-20px)' }
              }
            }}
          />
        ))}
        
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          AI GenArt Studio
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          We're working hard to bring you an amazing AI image generation experience!
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          {[
            { icon: '🎨', text: 'AI Art Generation' },
            { icon: '✨', text: 'Style Transfer' },
            { icon: '🖼️', text: 'Image Editing' },
            { icon: '🚀', text: 'Batch Processing' }
          ].map((feature, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                textAlign: 'center',
                background: theme.palette.background.paper,
                borderRadius: 3,
                minWidth: 100,
                animation: `slideUp 0.5s ease ${index * 0.1}s`,
                '@keyframes slideUp': {
                  '0%': { transform: 'translateY(20px)', opacity: 0 },
                  '100%': { transform: 'translateY(0)', opacity: 1 }
                }
              }}
            >
              <Typography variant="h4" sx={{ mb: 1 }}>{feature.icon}</Typography>
              <Typography variant="caption">{feature.text}</Typography>
            </Paper>
          ))}
        </Box>
        
        {/* Progress bar animation */}
        <Box sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption">Development Progress</Typography>
            <Typography variant="caption" sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 'bold'
            }}>
              75%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={75}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                animation: 'progressShine 2s linear infinite',
                '@keyframes progressShine': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '100%': { backgroundPosition: '100% 50%' }
                }
              }
            }}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          "Great things are coming. Stay tuned for updates!"
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 4, position: 'relative', zIndex: 1 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: `0 12px 30px ${theme.palette.primary.main}60`,
            }
          }}
        >
          Got It!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Animated Background Component
const AIIntelligenceBackground = ({ children }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
      {/* Animated gradient background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          background: `linear-gradient(135deg, 
            ${theme.palette.primary.light}10 0%, 
            ${theme.palette.secondary.light}15 25%, 
            ${theme.palette.info.light}10 50%, 
            ${theme.palette.success.light}15 75%, 
            ${theme.palette.warning.light}10 100%)`,
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 0%' },
            '50%': { backgroundPosition: '100% 100%' },
            '100%': { backgroundPosition: '0% 0%' }
          }
        }}
      />

      {/* Animated floating particles - Neural network nodes */}
      {[...Array(20)].map((_, i) => (
        <Box
          key={`node-${i}`}
          sx={{
            position: 'fixed',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: theme.palette.primary.main,
            opacity: 0.15,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `floatNode ${8 + Math.random() * 10}s linear infinite`,
            animationDelay: `${Math.random() * 5}s`,
            '@keyframes floatNode': {
              '0%': {
                transform: 'translate(0, 0) scale(1)',
                opacity: 0.1
              },
              '25%': {
                transform: 'translate(50px, -30px) scale(1.2)',
                opacity: 0.2
              },
              '50%': {
                transform: 'translate(100px, 0) scale(1)',
                opacity: 0.15
              },
              '75%': {
                transform: 'translate(50px, 30px) scale(0.8)',
                opacity: 0.1
              },
              '100%': {
                transform: 'translate(0, 0) scale(1)',
                opacity: 0.1
              }
            }
          }}
        />
      ))}

      {/* Animated connection lines between nodes */}
      <svg
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          opacity: 0.1,
          pointerEvents: 'none'
        }}
      >
        {[...Array(15)].map((_, i) => {
          const x1 = Math.random() * 100;
          const y1 = Math.random() * 100;
          const x2 = Math.random() * 100;
          const y2 = Math.random() * 100;
          
          return (
            <line
              key={`line-${i}`}
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
              stroke={theme.palette.primary.main}
              strokeWidth="1"
              strokeDasharray="5,5"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="0;50"
                dur={`${8 + Math.random() * 10}s`}
                repeatCount="indefinite"
              />
            </line>
          );
        })}
      </svg>

      {/* Floating AI-related icons */}
      <Box
        sx={{
          position: 'fixed',
          width: '100%',
          height: '100%',
          zIndex: 0,
          opacity: 0.1,
          pointerEvents: 'none'
        }}
      >
        {/* Brain icon */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            fontSize: '3rem',
            animation: 'floatIcon 8s ease-in-out infinite',
            '@keyframes floatIcon': {
              '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
              '50%': { transform: 'translateY(-20px) rotate(5deg)' }
            }
          }}
        >
          🧠
        </Box>
        
        {/* Robot icon */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '8%',
            fontSize: '2.5rem',
            animation: 'floatIcon 7s ease-in-out infinite',
            animationDelay: '1s'
          }}
        >
          🤖
        </Box>
        
        {/* Sparkles icon */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '12%',
            fontSize: '2rem',
            animation: 'floatIcon 6s ease-in-out infinite',
            animationDelay: '2s'
          }}
        >
          ✨
        </Box>
        
        {/* Chat bubble icon */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '25%',
            left: '10%',
            fontSize: '2.2rem',
            animation: 'floatIcon 9s ease-in-out infinite',
            animationDelay: '0.5s'
          }}
        >
          💬
        </Box>
        
        {/* Image icon */}
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '15%',
            fontSize: '2rem',
            animation: 'floatIcon 7.5s ease-in-out infinite',
            animationDelay: '1.5s'
          }}
        >
          🖼️
        </Box>
      </Box>

      {/* Pulsing circles for AI activity visualization */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '300px',
          height: '300px',
          zIndex: 0,
          opacity: 0.1,
          pointerEvents: 'none'
        }}
      >
        {[...Array(3)].map((_, i) => (
          <Box
            key={`pulse-${i}`}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: `2px solid ${theme.palette.primary.main}`,
              animation: `pulseRing ${2 + i * 1.5}s ease-out infinite`,
              animationDelay: `${i * 0.5}s`,
              '@keyframes pulseRing': {
                '0%': {
                  width: '30px',
                  height: '30px',
                  opacity: 0.8
                },
                '100%': {
                  width: '300px',
                  height: '300px',
                  opacity: 0
                }
              }
            }}
          />
        ))}
      </Box>

      {/* Content overlay */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {children}
      </Box>
    </Box>
  );
};

// // Screen info overlay component (DRAGGABLE VERSION)
// const ScreenInfoOverlay = ({ activeTab }) => {
//   const cardRef = useRef(null);

//   const [position, setPosition] = useState({ x: 300, y: 200 });
//   const [dragging, setDragging] = useState(false);
//   const [offset, setOffset] = useState({ x: 0, y: 0 });

//   const getScreenInfo = () => {
//     switch (activeTab) {
//       case "generator":
//         return {
//           title: "AI GenArt Studio",
//           description:
//             "Transform your ideas into stunning visuals using advanced AI image generation",
//           icon: "🎨",
//           color: "#3B82F6",
//         };
//       case "chatbot":
//         return {
//           title: "AI SmartDesk",
//           description:
//             "Intelligent conversations and task assistance with context-aware AI",
//           icon: "💭",
//           color: "#8B5CF6",
//         };
//       case "humanizer":
//         return {
//           title: "Humanize ToneCraft",
//           description:
//             "Refine AI-generated text to sound more natural and human-like",
//           icon: "✍️",
//           color: "#10B981",
//         };
//       default:
//         return {
//           title: "AI Intelligence Hub",
//           description:
//             "Your central command for AI-powered creativity and productivity",
//           icon: "🚀",
//           color: "#6366F1",
//         };
//     }
//   };

//   const info = getScreenInfo();

//   // Drag Start
//   const handleMouseDown = (e) => {
//     setDragging(true);
//     const rect = cardRef.current.getBoundingClientRect();
//     setOffset({
//       x: e.clientX - rect.left,
//       y: e.clientY - rect.top,
//     });
//     document.body.style.userSelect = "none";
//   };

//   // Drag Move
//   const handleMouseMove = (e) => {
//     if (!dragging) return;

//     setPosition({
//       x: e.clientX - offset.x,
//       y: e.clientY - offset.y,
//     });
//   };

//   // Drag End
//   const handleMouseUp = () => {
//     setDragging(false);
//     document.body.style.userSelect = "auto";
//   };

//   useEffect(() => {
//     window.addEventListener("mousemove", handleMouseMove);
//     window.addEventListener("mouseup", handleMouseUp);

//     return () => {
//       window.removeEventListener("mousemove", handleMouseMove);
//       window.removeEventListener("mouseup", handleMouseUp);
//     };
//   });

//   return (
//     <Box
//       ref={cardRef}
//       sx={{
//         position: "fixed",
//         left: position.x,
//         top: position.y,
//         zIndex: 2000,
//         background: "rgba(255,255,255,0.95)",
//         backdropFilter: "blur(10px)",
//         borderRadius: 2,
//         p: 2,
//         width: 280,
//         boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
//         border: `2px solid ${info.color}20`,
//         cursor: dragging ? "grabbing" : "default",
//       }}
//     >
//       {/* Drag Header */}
//       <Box
//         onMouseDown={handleMouseDown}
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           gap: 1,
//           mb: 1,
//           cursor: "grab",
//         }}
//       >
//         <Box sx={{ fontSize: "1.5rem" }}>{info.icon}</Box>
//         <Typography
//           variant="subtitle1"
//           sx={{ color: info.color, fontWeight: 600 }}
//         >
//           {info.title}
//         </Typography>
//       </Box>

//       <Typography variant="body2" color="text.secondary">
//         {info.description}
//       </Typography>

//       {/* Active Indicator */}
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           gap: 1,
//           mt: 1,
//           pt: 1,
//           borderTop: "1px solid #e0e0e0",
//         }}
//       >
//         <Box
//           sx={{
//             width: 8,
//             height: 8,
//             borderRadius: "50%",
//             background: info.color,
//             animation: "blink 1.5s ease infinite",
//             "@keyframes blink": {
//               "0%, 100%": { opacity: 1 },
//               "50%": { opacity: 0.3 },
//             },
//           }}
//         />
//         <Typography variant="caption" color="text.secondary">
//           Currently active
//         </Typography>
//       </Box>
//     </Box>
//   );
// };


// Screen info overlay component (DRAGGABLE VERSION WITH CLOSE)
const ScreenInfoOverlay = ({ activeTab }) => {
  const cardRef = useRef(null);

  const [position, setPosition] = useState({ x: 300, y: 200 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // ✅ NEW: control visibility (resets on refresh automatically)
  const [isVisible, setIsVisible] = useState(true);

  const getScreenInfo = () => {
    switch (activeTab) {
      case "generator":
        return {
          title: "AI GenArt Studio",
          description:
            "Transform your ideas into stunning visuals using advanced AI image generation",
          icon: "🎨",
          color: "#3B82F6",
        };
      case "chatbot":
        return {
          title: "AI SmartDesk",
          description:
            "Intelligent conversations and task assistance with context-aware AI",
          icon: "💭",
          color: "#8B5CF6",
        };
      case "humanizer":
        return {
          title: "Humanize ToneCraft",
          description:
            "Refine AI-generated text to sound more natural and human-like",
          icon: "✍️",
          color: "#10B981",
        };
      default:
        return {
          title: "AI Intelligence Hub",
          description:
            "Your central command for AI-powered creativity and productivity",
          icon: "🚀",
          color: "#6366F1",
        };
    }
  };

  const info = getScreenInfo();

  // Drag Start
  const handleMouseDown = (e) => {
    setDragging(true);
    const rect = cardRef.current.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    document.body.style.userSelect = "none";
  };

  // Drag Move
  const handleMouseMove = (e) => {
    if (!dragging) return;

    setPosition({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    });
  };

  // Drag End
  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = "auto";
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  // ✅ If closed, don't render
  if (!isVisible) return null;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 2000,
        background: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: 2,
        p: 2,
        width: 280,
        boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
        border: `2px solid ${info.color}20`,
        cursor: dragging ? "grabbing" : "default",
      }}
    >
      {/* Header with Close Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        {/* Drag Header */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "grab",
          }}
        >
          <Box sx={{ fontSize: "1.5rem" }}>{info.icon}</Box>
          <Typography
            variant="subtitle1"
            sx={{ color: info.color, fontWeight: 600 }}
          >
            {info.title}
          </Typography>
        </Box>

        {/* ✅ Close Icon */}
        <IconButton
          size="small"
          onClick={() => setIsVisible(false)}
          sx={{
            color: "grey.600",
            "&:hover": {
              color: "error.main",
              backgroundColor: "rgba(244,67,54,0.1)",
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {info.description}
      </Typography>

      {/* Active Indicator */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          pt: 1,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: info.color,
            animation: "blink 1.5s ease infinite",
            "@keyframes blink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Currently active
        </Typography>
      </Box>
    </Box>
  );
};

const HumanizerTab = ({ 
  humanizerLevel, 
  setHumanizerLevel, 
  userID, 
  accessToLight,
  accessToBalanced,
  accessToMaximum,
  userPlan
}) => {
  const [humanizeText, setHumanizeText] = useState("");
  const [humanizedResult, setHumanizedResult] = useState("");
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);

  // Get word limit based on user plan
  const getMaxWords = () => {
    return userPlan === 'premium' ? MAX_CHAT_WORDS_PREMIUM : MAX_CHAT_WORDS;
  };

  // Check if humanizer level is accessible
  const isLevelAccessible = (level) => {
    switch(level) {
      case 'light': return accessToLight;
      case 'balanced': return accessToBalanced;
      case 'aggressive': return accessToMaximum;
      default: return true;
    }
  };

  const handleHumanizeText = async () => {
    if (!humanizeText.trim()) {
      toast.warning("Please enter text to humanize", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Check word limit
    const wordCount = humanizeText.trim().split(/\s+/).length;
    const maxWords = getMaxWords();
    if (wordCount > maxWords) {
      toast.error(`Text exceeds ${maxWords} word limit`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Check AI request limit
    // const checkailimit = await checkAIRequestLimit(userID);
    // if (!checkailimit) {
    //   toast.warn("AI request limit reached.");
    //   return;
    // }

    // Check level access
    if (!isLevelAccessible(humanizerLevel)) {
      toast.error(`"${humanizerLevel}" humanization level is not available in your plan`);
      return;
    }

    setIsHumanizing(true);
    setLoading(true);
    setHumanizedResult("");

    try {
      const response = await aiHumanizer(humanizeText, humanizerLevel);
      let humanizedText = "";
      
      if (response.data && response.data.data && response.data.data.humanized_text) {
        humanizedText = response.data.data.humanized_text;
      } else if (response.data && response.data.humanized_text) {
        humanizedText = response.data.humanized_text;
      } else if (response.data && typeof response.data === "string") {
        humanizedText = response.data;
      } else if (response && response.humanized_text) {
        humanizedText = response.humanized_text;
      } else {
        humanizedText = "Received response but couldn't parse the humanized text.";
      }

      setHumanizedResult(humanizedText);
      
      // Add user log
      const logData = {
        userId: sessionStorage.getItem("userId"),
        organizationId: sessionStorage.getItem("organizationId"),
        actionType: "humanizer",
      };
      await addUserLogs(logData);

      toast.success("Text humanized successfully!", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Error calling AI Humanizer:", error);
      const errorMsg = error.response?.data?.message || error.message || "Unknown error";
      setHumanizedResult(
        "Sorry, something went wrong while humanizing the text. Error: " + errorMsg
      );
      toast.error(`Failed to humanize text: ${errorMsg}`, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsHumanizing(false);
      setLoading(false);
    }
  };

  const handleClearText = () => {
    setHumanizeText("");
  };

  const handleClearResult = () => {
    setHumanizedResult("");
  };

  const handleToggleFullscreen = () => {
    setFullscreenDialogOpen(true);
  };

  const handleCopyResult = () => {
    if (!humanizedResult) return;
    navigator.clipboard
      .writeText(humanizedResult)
      .then(() => {
        toast.success("Copied to clipboard!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy to clipboard", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      });
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    const wordCount = newText.trim().split(/\s+/).length;
    const maxWords = getMaxWords();
    
    if (wordCount > maxWords) {
      // Trim to max words
      const words = newText.trim().split(/\s+/);
      const trimmedText = words.slice(0, maxWords).join(' ');
      setHumanizeText(trimmedText);
      toast.warning(`Text trimmed to ${maxWords} words`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      setHumanizeText(newText);
    }
  };

  const handlePaste = async (e) => {
    e.preventDefault();
    try {
      const pastedText = await navigator.clipboard.readText();
      const currentText = humanizeText;
      const currentWords = currentText.trim().split(/\s+/).length;
      const maxWords = getMaxWords();
      const remainingWords = maxWords - currentWords;
      
      if (remainingWords <= 0) {
        toast.warning(
          `Cannot paste - text already at ${maxWords} word limit`,
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return;
      }

      const pastedWords = pastedText.trim().split(/\s+/);
      const wordsToAdd = pastedWords.slice(0, remainingWords).join(' ');
      const newText = currentText + (currentText ? ' ' : '') + wordsToAdd;
      setHumanizeText(newText);
      
      if (pastedWords.length > remainingWords) {
        toast.warning(
          `Pasted text trimmed - only ${remainingWords} words available`,
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
    } catch (error) {
      console.error("Error reading clipboard:", error);
      const pastedText = e.clipboardData.getData("text");
      const currentText = humanizeText;
      const currentWords = currentText.trim().split(/\s+/).length;
      const maxWords = getMaxWords();
      const remainingWords = maxWords - currentWords;
      const pastedWords = pastedText.trim().split(/\s+/);
      const wordsToAdd = pastedWords.slice(0, remainingWords).join(' ');
      const newText = currentText + (currentText ? ' ' : '') + wordsToAdd;
      setHumanizeText(newText);
    }
  };

  const isTextValid = humanizeText.trim().length > 0 && 
    humanizeText.trim().split(/\s+/).length <= getMaxWords();

  return (
    <Grid container spacing={2}>
      <Grid xs={12} md={8} lg={8}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon color="success" />
                <Typography variant="h6">AI Humanizer</Typography>
              </Box>
              {(humanizeText || humanizedResult) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Clear />}
                  onClick={() => {
                    handleClearText();
                    handleClearResult();
                  }}
                  sx={{
                    borderColor: "black.400",
                    color: "#1976d2",
                    "&:hover": {
                      borderColor: "black.600",
                      backgroundColor: "black.100",
                    },
                  }}
                >
                  Clear All
                </Button>
              )}
            </Box>
            
            <Typography variant="subtitle2" gutterBottom>
              Humanization Level
            </Typography>
            
            <ToggleButtonGroup
              value={humanizerLevel}
              exclusive
              onChange={(e, val) => {
                if (val && isLevelAccessible(val)) {
                  setHumanizerLevel(val);
                } else if (val) {
                  toast.error(`"${val}" humanization level is not available in your plan`);
                }
              }}
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton
                value="light"
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "rgba(11, 194, 245, 0.3)",
                    borderColor: "#20b7e5ff",
                    color: "#1644fbff",
                    "&:hover": {
                      backgroundColor: "rgba(171, 237, 234, 0.28)",
                    },
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
                disabled={!accessToLight}
              >
                <Box textAlign="center">
                  <Typography variant="body2" fontWeight="bold" color={"#1644fbff"}>
                    Light
                  </Typography>
                  <Typography variant="caption" color={"#1644fbff"}>
                    Minimal changes
                    {!accessToLight && " (Premium)"}
                  </Typography>
                </Box>
              </ToggleButton>
              
              <ToggleButton
                value="balanced"
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                    borderColor: "#10B981",
                    color: "#065F46",
                    "&:hover": {
                      backgroundColor: "rgba(16, 185, 129, 0.15)",
                    },
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
                disabled={!accessToBalanced}
              >
                <Box textAlign="center">
                  <Typography variant="body2" fontWeight="bold" color={"#065F46"}>
                    Balanced
                  </Typography>
                  <Typography variant="caption" color={"#065F46"}>
                    Optimal balance
                    {!accessToBalanced && " (Premium)"}
                  </Typography>
                </Box>
              </ToggleButton>
              
              <ToggleButton
                value="aggressive"
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "rgba(217, 57, 253, 0.26)",
                    borderColor: "#a86afaff",
                    color: "#a905fbff",
                    "&:hover": {
                      backgroundColor: "rgba(225, 68, 239, 0.15)",
                    },
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
                disabled={!accessToMaximum}
              >
                <Box textAlign="center">
                  <Typography variant="body2" fontWeight="bold" color={"#a905fbff"}>
                    Maximum
                  </Typography>
                  <Typography variant="caption" color={"#ab07fdff"}>
                    Maximum humanization
                    {!accessToMaximum && " (Premium)"}
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
            
            <Typography variant="subtitle2" gutterBottom>
              AI-Generated Text to Humanize
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={8}
              columns={19}
              placeholder="Paste your AI-generated text here..."
              variant="outlined"
              value={humanizeText}
              onChange={handleTextChange}
              onPaste={handlePaste}
              inputRef={textareaRef}
              sx={{ mb: 1 }}
              error={humanizeText.trim().split(/\s+/).length > getMaxWords()}
              helperText={
                humanizeText.trim().split(/\s+/).length > getMaxWords()
                  ? `Word limit exceeded (${getMaxWords()} max)`
                  : ""
              }
            />
            
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color={
                  humanizeText.trim().split(/\s+/).length > getMaxWords()
                    ? "error"
                    : "text.primary"
                }
              >
                {humanizeText.trim().split(/\s+/).length} / {getMaxWords()} words
              </Typography>
              {humanizeText.trim().split(/\s+/).length > getMaxWords() && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Warning fontSize="small" color="error" />
                  <Typography variant="caption" color="error">
                    Limit exceeded
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<AutoAwesome />}
              onClick={handleHumanizeText}
              disabled={loading || !isTextValid}
              sx={{
                background: "linear-gradient(90deg, #10B981 0%, #059669 100%)",
                py: 1.5,
                "&:disabled": {
                  background: "black.400",
                },
              }}
            >
              {loading ? "Humanizing..." : "Humanize Text"}
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Output Section */}
      <Grid xs={12} md={4} lg={4}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AutoAwesome color="success" />
                <Typography variant="h6">Humanized Result</Typography>
              </Box>
              {humanizedResult && (
                <IconButton
                  size="small"
                  onClick={handleToggleFullscreen}
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.50",
                    },
                  }}
                  title="Expand to full screen"
                >
                  <Fullscreen fontSize="small" />
                </IconButton>
              )}
            </Box>
            
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.primary">
                  Humanizing your text...
                </Typography>
              </Box>
            ) : humanizedResult ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "success.50",
                  borderColor: "success.200",
                  minHeight: 200,
                  maxHeight: 300,
                  overflow: "auto",
                  position: "relative",
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {humanizedResult}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={handleCopyResult}
                  >
                    Copy
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: "black.50",
                  minHeight: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.primary">
                  Your humanized text will appear here after processing.
                </Typography>
              </Paper>
            )}
            
            {/* Word Count Comparison */}
            {humanizedResult && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "black.50", borderRadius: 1 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Word Count:
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.primary">
                    Original: {humanizeText.trim().split(/\s+/).length} words
                  </Typography>
                  <Typography variant="caption" color="text.primary">
                    Humanized: {humanizedResult.trim().split(/\s+/).length} words
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenDialogOpen}
        onClose={() => setFullscreenDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isFullscreen}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <AutoAwesome color="success" />
              <Typography variant="h6" sx={{ color: "primary.main" }}>
                Humanized Result - Full View
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                onClick={() => setIsFullscreen(!isFullscreen)}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                sx={{ color: "#1976d2" }}
              >
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setFullscreenDialogOpen(false)}
                title="Close"
                sx={{ color: "#f44336" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {humanizedResult && (
            <Box sx={{ p: 3, height: "100%" }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: "success.50",
                  borderColor: "success.200",
                  minHeight: 400,
                  maxHeight: isFullscreen ? "calc(100vh - 200px)" : 500,
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.6,
                  }}
                >
                  {humanizedResult}
                </Typography>
              </Paper>
              
              <Box sx={{ mt: 3, p: 2, bgcolor: "black.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Text Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Box>
                      <Typography variant="caption" color="text.primary" display="block">
                        Original Text
                      </Typography>
                      <Typography variant="body2">
                        {humanizeText.trim().split(/\s+/).length} words
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {humanizeText.length} characters
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid xs={6}>
                    <Box>
                      <Typography variant="caption" color="text.primary" display="block">
                        Humanized Text
                      </Typography>
                      <Typography variant="body2">
                        {humanizedResult.trim().split(/\s+/).length} words
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {humanizedResult.length} characters
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Box>
            <Button onClick={() => setFullscreenDialogOpen(false)} variant="outlined" sx={{ mr: 1 }}>
              Close
            </Button>
            <Button
              onClick={() => setIsFullscreen(!isFullscreen)}
              variant="contained"
              startIcon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            >
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default function AIIntelligenzApp() {
  // Redux user details
  const userDetails = useSelector((state) => state.user);
  const userID = userDetails?.currentUser?._id || userDetails?.user?.currentUser?._id;
  const userPlan = userDetails?.currentUser?.plan || 'basic'; // Assuming plan is stored in user object
  
  // Access control hooks
  const accessToAIGenArtStudio = useCheckFeatureAccess("aiIntelligenz", "genArtStudio");
  const accessToArtStyle = useCheckFeatureAccess("aiIntelligenz", "artStyle");
  const accessToAISmartDesk = useCheckFeatureAccess("aiIntelligenz", "smartDesk");
  const accessToHumanizer = useCheckFeatureAccess("aiIntelligenz", "humanizer");
  const accessToLightLevel = useCheckFeatureAccess("aiIntelligenz", "humanizerLight");
  const accessToBalancedLevel = useCheckFeatureAccess("aiIntelligenz", "humanizerBalanced");
  const accessToMaximumLevel = useCheckFeatureAccess("aiIntelligenz", "humanizerMaximum");
  
  const accessToAIIntelligenzScreen = useCheckSubscriptionPlanAccess("AI Image Generator");
  const [subReqOpen, setSubReqOpen] = useState(false);
  
  // State for Coming Soon popup
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  
  useEffect(() => {
    if (accessToAIIntelligenzScreen === false) {
      setSubReqOpen(true);
    }
  }, [accessToAIIntelligenzScreen]);

  const [activeTab, setActiveTab] = useState("chatbot");
  const [artStyle, setArtStyle] = useState("realistic");
  const [humanizerLevel, setHumanizerLevel] = useState("light");
  const [imagePrompt, setImagePrompt] = useState("");
  const [chatMessage, setChatMessage] = useState("");
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMessageContent, setEditMessageContent] = useState("");
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState("");
  const [globalToast, setGlobalToast] = useState({ open: false, message: "", severity: "info" });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // Get word limit for chat based on user plan
  const getChatWordLimit = () => {
    return userPlan === 'premium' ? MAX_CHAT_WORDS_PREMIUM : MAX_CHAT_WORDS;
  };

  const showGlobalToast = (message, severity = "info") => {
    setGlobalToast({ open: true, message, severity });
  };

  const handleCloseGlobalToast = () => {
    setGlobalToast({ ...globalToast, open: false });
  };

  const generateId = () => crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random().toString(36).slice(2, 10);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation, isLoading]);

  useEffect(() => {
    const savedChats = localStorage.getItem("aiChatHistory");
    if (savedChats) {
      try {
        const parsedChats = JSON.parse(savedChats).map((chat) => ({
          ...chat,
          timestamp: new Date(chat.timestamp),
          messages: chat.messages.map((msg) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })),
        }));
        console.log("Loaded chat history:", parsedChats);
        setChatHistory(parsedChats);
        showGlobalToast("Chat history loaded successfully", "success");
      } catch (error) {
        console.error("Error parsing chat history:", error);
        setChatHistory([]);
        showGlobalToast("Error loading chat history", "error");
      }
    }
  }, []);

  const saveChatToHistory = (messages, title = "New Chat") => {
    const newChat = {
      id: generateId(),
      title: title,
      messages: [...messages],
      timestamp: new Date(),
      pinned: false,
    };
    console.log("Saving new chat:", newChat);
    setChatHistory((prev) => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
    showGlobalToast("New chat started", "success");
    return newChat.id;
  };

  const updateChatHistory = (chatId, updatedMessages, updatedTitle = null) => {
    setChatHistory((prevHistory) =>
      prevHistory.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            messages: updatedMessages,
            title: updatedTitle ?? chat.title,
            timestamp: new Date(),
          };
        }
        return chat;
      })
    );
  };

  const handleSendMessage = async (message = chatMessage, isQuickPrompt = false) => {
    // Check feature access
    if (!accessToAISmartDesk) {
      toast.warn("AI SmartDesk feature is not available in your plan");
      return;
    }

    const finalMessage = message?.trim() || chatMessage?.trim();
    if (!finalMessage) {
      showGlobalToast("Please enter a message", "warning");
      return;
    }

    // Check word limit
    const wordCount = finalMessage.split(/\s+/).length;
    const maxWords = getChatWordLimit();
    if (wordCount > maxWords) {
      toast.warn(`Message exceeds ${maxWords} word limit`);
      return;
    }

    // Check AI request limit
    // const checkailimit = await checkAIRequestLimit(userID);
    // if (!checkailimit) {
    //   toast.warn("AI request limit reached.");
    //   return;
    // }

    const userMessage = {
      id: generateId(),
      type: "user",
      content: finalMessage,
      timestamp: new Date(),
    };

    const updatedConversation = [...conversation, userMessage];
    setConversation(updatedConversation);
    setChatMessage("");
    setIsLoading(true);

    let chatId = currentChatId;
    if (!currentChatId) {
      const firstFewWords = finalMessage.split(" ").slice(0, 5).join(" ");
      const title = firstFewWords + (finalMessage.split(" ").length > 5 ? "..." : "");
      chatId = saveChatToHistory([userMessage], title);
      console.log("Created new chat with ID:", chatId);
    } else {
      updateChatHistory(currentChatId, updatedConversation);
      console.log("Updated existing chat:", currentChatId);
    }

    try {
      const response = await aiSmartDesk(finalMessage, "");
      let aiResponseText = "";
      
      if (response.data && response.data.data && response.data.data.response) {
        aiResponseText = response.data.data.response;
      } else if (response.data && response.data.response) {
        aiResponseText = response.data.response;
      } else if (response.data && typeof response.data === "string") {
        aiResponseText = response.data;
      } else if (response && response.response) {
        aiResponseText = response.response;
      } else {
        aiResponseText = "Received response but couldn't parse it.";
      }

      const aiMessage = {
        id: generateId(),
        type: "ai",
        content: aiResponseText,
        timestamp: new Date(),
      };

      const finalConversation = [...updatedConversation, aiMessage];
      setConversation(finalConversation);
      updateChatHistory(chatId, finalConversation);
      
      // Add user log
      const logData = {
        userId: sessionStorage.getItem("userId"),
        organizationId: sessionStorage.getItem("organizationId"),
        actionType: "smartDesk",
      };
      await addUserLogs(logData);

    } catch (error) {
      console.error("Error calling AI SmartDesk:", error);
      const errorMessage = {
        id: generateId(),
        type: "ai",
        content: "Sorry, something went wrong. Please try again. Error: " + (error.response?.data?.message || error.message),
        timestamp: new Date(),
        isError: true,
      };
      const finalConversation = [...updatedConversation, errorMessage];
      setConversation(finalConversation);
      if (chatId) {
        updateChatHistory(chatId, finalConversation);
      }
      showGlobalToast("Failed to get AI response", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    handleSendMessage(prompt, true);
  };

  const handleMessageMenuOpen = (event, message) => {
    setMessageMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null);
    setSelectedMessage(null);
  };

  const handleCopyMessage = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.content);
      setSuccessMessage("Message copied to clipboard!");
      setShowSuccess(true);
      handleMessageMenuClose();
    }
  };

  const handleEditMessage = () => {
    if (selectedMessage) {
      setEditMessageContent(selectedMessage.content);
      setEditDialogOpen(true);
      handleMessageMenuClose();
    }
  };

  useEffect(() => {
    if (chatHistory.length > 0) {
      localStorage.setItem("aiChatHistory", JSON.stringify(chatHistory));
    } else {
      localStorage.removeItem("aiChatHistory");
    }
  }, [chatHistory]);

  const handleSaveEdit = async () => {
    if (!selectedMessage || selectedMessage.type !== "user") return;
    
    const editedContent = editMessageContent.trim();
    if (!editedContent) {
      setEditDialogOpen(false);
      setEditMessageContent("");
      return;
    }

    // Check word limit
    const wordCount = editedContent.split(/\s+/).length;
    const maxWords = getChatWordLimit();
    if (wordCount > maxWords) {
      toast.warn(`Edited message exceeds ${maxWords} word limit`);
      return;
    }

    const editIndex = conversation.findIndex((msg) => msg.id === selectedMessage.id);
    if (editIndex === -1) return;

    const newConversation = [
      ...conversation.slice(0, editIndex),
      { ...selectedMessage, content: editedContent },
    ];
    
    setConversation(newConversation);
    setIsLoading(true);
    setEditDialogOpen(false);
    setEditMessageContent("");

    let chatId = currentChatId;
    if (chatId) {
      updateChatHistory(chatId, newConversation);
    }

    try {
      const response = await aiSmartDesk(editedContent, "");
      let aiResponseText = "";
      
      if (response.data?.data?.response) {
        aiResponseText = response.data.data.response;
      } else if (response.data?.response) {
        aiResponseText = response.data.response;
      } else if (typeof response.data === "string") {
        aiResponseText = response.data;
      } else if (response?.response) {
        aiResponseText = response.response;
      } else {
        aiResponseText = "Received response but couldn't parse it.";
      }

      const aiMessage = {
        id: generateId(),
        type: "ai",
        content: aiResponseText,
        timestamp: new Date(),
      };

      const finalConversation = [...newConversation, aiMessage];
      setConversation(finalConversation);
      if (chatId) {
        updateChatHistory(chatId, finalConversation);
      }
      
      setSuccessMessage("Message edited and regenerated!");
      setShowSuccess(true);
      
    } catch (error) {
      console.error("Error regenerating AI response:", error);
      const errorMessage = {
        id: Date.now(),
        type: "ai",
        content: "Sorry, something went wrong while regenerating. Error: " + (error.response?.data?.message || error.message),
        timestamp: new Date(),
        isError: true,
      };
      setConversation([...newConversation, errorMessage]);
      if (chatId) {
        updateChatHistory(chatId, [...newConversation, errorMessage]);
      }
      showGlobalToast("Failed to regenerate response", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatMenuOpen = (event, chat) => {
    setChatMenuAnchor(event.currentTarget);
    setSelectedChat(chat);
  };

  const handleChatMenuClose = () => {
    setChatMenuAnchor(null);
    setSelectedChat(null);
  };

  const handleRenameChat = () => {
    if (selectedChat) {
      setNewChatTitle(selectedChat.title);
      setRenameDialogOpen(true);
      handleChatMenuClose();
    }
  };

  const handleSaveRename = () => {
    if (!selectedChat || !newChatTitle.trim()) {
      console.warn("No chat selected or empty title");
      showGlobalToast("Please enter a valid title", "warning");
      return;
    }

    const updatedTitle = newChatTitle.trim();
    console.log("Renaming chat:", selectedChat.id, "to:", updatedTitle);
    
    const updatedHistory = chatHistory.map((chat) => {
      if (chat.id === selectedChat.id) {
        return { ...chat, title: updatedTitle, timestamp: new Date() };
      }
      return chat;
    });
    
    setChatHistory(updatedHistory);
    setRenameDialogOpen(false);
    setNewChatTitle("");
    setSelectedChat(null);
    
    setSuccessMessage("Chat renamed successfully!");
    setShowSuccess(true);
  };

  const handleDeleteChat = () => {
    if (selectedChat) {
      const updatedHistory = chatHistory.filter((chat) => chat.id !== selectedChat.id);
      setChatHistory(updatedHistory);
      if (currentChatId === selectedChat.id) {
        setCurrentChatId(null);
        setConversation([]);
      }
      handleChatMenuClose();
      
      setSuccessMessage("Chat deleted successfully!");
      setShowSuccess(true);
    }
  };

  const handlePinChat = () => {
    if (selectedChat) {
      const updatedHistory = chatHistory.map((chat) =>
        chat.id === selectedChat.id ? { ...chat, pinned: !chat.pinned } : chat
      );
      setChatHistory(updatedHistory);
      handleChatMenuClose();
      
      setSuccessMessage(selectedChat.pinned ? "Chat unpinned" : "Chat pinned");
      setShowSuccess(true);
    }
  };

  const handleShareChat = () => {
    if (selectedChat) {
      const chatData = {
        title: selectedChat.title,
        messages: selectedChat.messages,
        timestamp: selectedChat.timestamp,
      };
      navigator.clipboard.writeText(JSON.stringify(chatData, null, 2));
      handleChatMenuClose();
      
      setSuccessMessage("Chat data copied to clipboard!");
      setShowSuccess(true);
    }
  };

  const loadChat = (chat) => {
    console.log("Loading chat:", chat);
    setConversation(chat.messages || []);
    setCurrentChatId(chat.id);
  };

  const startNewChat = () => {
    console.log("Starting new chat");
    setConversation([]);
    setCurrentChatId(null);
    
    setSuccessMessage("New chat started!");
    setShowSuccess(true);
  };

  const getChatPreview = (chat) => {
    if (!chat.messages || chat.messages.length === 0) return "Empty chat";
    
    const userMessages = chat.messages.filter((msg) => msg.type === "user");
    if (userMessages.length > 0) {
      const lastUserMessage = userMessages[userMessages.length - 1];
      const preview = lastUserMessage.content.split(" ").slice(0, 8).join(" ");
      return preview + (lastUserMessage.content.split(" ").length > 8 ? "..." : "");
    }
    
    const aiMessages = chat.messages.filter((msg) => msg.type === "ai");
    if (aiMessages.length > 0) {
      const lastAiMessage = aiMessages[aiMessages.length - 1];
      const preview = lastAiMessage.content.split(" ").slice(0, 8).join(" ");
      return "AI: " + preview + (lastAiMessage.content.split(" ").length > 8 ? "..." : "");
    }
    
    return "Empty chat";
  };

  const getSortedChatHistory = () => {
    return [...chatHistory].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  };

  const handleGenArtStudioClick = () => {
    // Show coming soon popup instead of switching tab
    setComingSoonOpen(true);
  };

  const renderImageGenerator = () => {
    // This will now just show a placeholder or coming soon message
    return (
      <Grid container xs={12} lg={12} spacing={1}>
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Box textAlign="center" py={4}>
                <ImageIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
                <Typography variant="h5" color="primary.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                  AI GenArt Studio
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Transform your ideas into stunning visuals using advanced AI image generation
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AutoAwesome />}
                  onClick={() => setComingSoonOpen(true)}
                  sx={{
                    mt: 2,
                    background: "linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)",
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      background: "linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)",
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderChatHistory = () => (
    <Card sx={{ height: 480, overflow: "auto" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Chat History</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={startNewChat}
            startIcon={<ChatIcon />}
          >
            New Chat
          </Button>
        </Box>
        
        {chatHistory.length === 0 ? (
          <Box textAlign="center" py={4}>
            <History sx={{ fontSize: 48, color: "black.400", mb: 2 }} />
            <Typography variant="body2" color="text.primary">
              No chat history yet. Start a conversation!
            </Typography>
          </Box>
        ) : (
          <List>
            {getSortedChatHistory().map((chat) => (
              <ListItem
                key={chat.id}
                sx={{
                  border: currentChatId === chat.id ? "2px solid #8B5CF6" : "1px solid #e0e0e0",
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: currentChatId === chat.id ? "#f3f4f6" : "white",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "#f9fafb",
                  },
                }}
                onClick={() => loadChat(chat)}
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChatMenuOpen(e, chat);
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {chat.pinned && <PushPin fontSize="small" color="primary" />}
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{
                          fontWeight: currentChatId === chat.id ? "bold" : "normal",
                        }}
                      >
                        {chat.title}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.primary">
                      {new Date(chat.timestamp).toLocaleDateString()} •{" "}
                      {chat.messages ? chat.messages.length : 0} messages
                      {chat.messages && chat.messages.length > 0 && (
                        <Box
                          component="span"
                          sx={{ display: "block", fontStyle: "italic", mt: 0.5 }}
                        >
                          {getChatPreview(chat)}
                        </Box>
                      )}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );

  const renderChatbot = () => {
    if (!accessToAISmartDesk) {
      return (
        <Grid container spacing={3}>
          <Grid xs={12}>
            <Card>
              <CardContent>
                <Box textAlign="center" py={4}>
                  <ChatIcon sx={{ fontSize: 64, color: "text.primary", mb: 2 }} />
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    AI SmartDesk Not Available
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    This feature is not included in your current plan. Upgrade to access AI chat.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={3}>
        {/* Chat History Sidebar */}
        <Grid xs={12} lg={3} md={4}>
          {renderChatHistory()}
        </Grid>
        
        {/* Main Chat Area */}
        <Grid xs={12} lg={9} md={8}>
          <Card sx={{ height: 480, display: "flex", flexDirection: "column" }}>
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                p: 0,
                "&:last-child": { pb: 0 },
              }}
            >
              {/* Header */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <ChatIcon color="primary" />
                  <Typography variant="h6">
                    {currentChatId
                      ? chatHistory.find((chat) => chat.id === currentChatId)?.title || "AI SmartDesk"
                      : "AI SmartDesk"}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.primary">
                  Word limit: {getChatWordLimit()}
                </Typography>
              </Box>
              
              {/* Chat Messages Area */}
              <Box
                flexGrow={1}
                p={2}
                sx={{
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  bgcolor: "black.50",
                  minHeight: 0,
                }}
              >
                {/* Welcome message when no conversation */}
                {conversation.length === 0 && !isLoading && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="80%"
                  >
                    <Paper
                      elevation={0}
                      sx={{ p: 3, bgcolor: "white", textAlign: "center", maxWidth: 400 }}
                    >
                      <Avatar sx={{ bgcolor: "#6366F1", mb: 2, mx: "auto" }}>
                        <AutoAwesome />
                      </Avatar>
                      <Typography variant="h6" gutterBottom>
                        Hello! I'm your AI assistant
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        How can I help you today? Ask me anything or try one of the quick prompts below.
                      </Typography>
                    </Paper>
                  </Box>
                )}
                
                {/* Conversation Messages */}
                {conversation.map((message) => (
                  <Box
                    key={message.id}
                    display="flex"
                    justifyContent={message.type === "user" ? "flex-end" : "flex-start"}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        maxWidth: "70%",
                        flexDirection: message.type === "user" ? "row-reverse" : "row",
                      }}
                    >
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: message.type === "user" ? "#3B82F6" : "#8B5CF6",
                          fontSize: "0.875rem",
                        }}
                      >
                        {message.type === "user" ? "U" : "AI"}
                      </Avatar>
                      
                      {/* Message Bubble */}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          bgcolor: message.type === "user" ? "#3B82F6" : "white",
                          color: message.type === "user" ? "white" : "text.primary",
                          borderRadius: 2,
                          position: "relative",
                          border: message.isError ? "1px solid #f44336" : "none",
                          "&:before":
                            message.type === "user"
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  top: 8,
                                  right: -6,
                                  width: 0,
                                  height: 0,
                                  borderLeft: "6px solid #3B82F6",
                                  borderTop: "6px solid transparent",
                                  borderBottom: "6px solid transparent",
                                }
                              : {
                                  content: '""',
                                  position: "absolute",
                                  top: 8,
                                  left: -6,
                                  width: 0,
                                  height: 0,
                                  borderRight: "6px solid white",
                                  borderTop: "6px solid transparent",
                                  borderBottom: "6px solid transparent",
                                },
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              color: message.type === "user" ? "black" : "text.primary",
                            }}
                          >
                            {message.content}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMessageMenuOpen(e, message)}
                            sx={{
                              ml: 1,
                              color: message.type === "user" ? "black" : "text.primary",
                              opacity: 0.7,
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            opacity: 0.7,
                            color: message.type === "user" ? "rgba(8, 8, 8, 1)" : "text.primary",
                          }}
                        >
                          {message.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                ))}
                
                {/* Loading Indicator */}
                {isLoading && (
                  <Box display="flex" justifyContent="flex-start">
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "#8B5CF6",
                        }}
                      >
                        AI
                      </Avatar>
                      <Paper elevation={1} sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <Typography variant="caption" color="text.primary">
                            Thinking...
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                )}
                
                <div ref={messagesEndRef} />
              </Box>
              
              {/* Quick Prompts - Show when conversation is empty */}
              {conversation.length === 0 && !isLoading && (
                <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quick Prompts:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {[
                      "Write a professional email",
                      "Create a marketing strategy",
                      "Debug this code",
                      "Summarize this article",
                      "Generate creative ideas",
                    ].map((prompt, idx) => (
                      <Chip
                        key={idx}
                        label={prompt}
                        variant="outlined"
                        size="small"
                        onClick={() => handleQuickPrompt(prompt)}
                        sx={{ cursor: "pointer", mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Input Area */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                <Box display="flex" gap={1} alignItems="flex-end">
                  <TextField
                    fullWidth
                    placeholder="Type your message here..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    size="small"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={3}
                    disabled={isLoading || !accessToAISmartDesk}
                    helperText={`${chatMessage.trim().split(/\s+/).length} / ${getChatWordLimit()} words`}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => handleSendMessage()}
                    disabled={isLoading || !chatMessage.trim() || !accessToAISmartDesk}
                    sx={{
                      bgcolor: "#8B5CF6",
                      color: "white",
                      "&:hover": { bgcolor: "#7C3AED" },
                      "&.Mui-disabled": {
                        bgcolor: "black.400",
                        color: "white",
                      },
                    }}
                  >
                    <Send />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Message Context Menu
  const renderMessageMenu = () => (
    <Menu
      anchorEl={messageMenuAnchor}
      open={Boolean(messageMenuAnchor)}
      onClose={handleMessageMenuClose}
    >
      <MenuItem onClick={handleCopyMessage}>
        <ListItemIcon>
          <ContentCopy fontSize="small" />
        </ListItemIcon>
        <ListItemText>Copy</ListItemText>
      </MenuItem>
      {selectedMessage?.type === "user" && (
        <MenuItem onClick={handleEditMessage}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  // Chat History Context Menu
  const renderChatMenu = () => (
    <Menu
      anchorEl={chatMenuAnchor}
      open={Boolean(chatMenuAnchor)}
      onClose={handleChatMenuClose}
    >
      <MenuItem onClick={handleRenameChat}>
        <ListItemIcon>
          <Edit fontSize="small" />
        </ListItemIcon>
        <ListItemText>Rename</ListItemText>
      </MenuItem>
      <MenuItem onClick={handlePinChat}>
        <ListItemIcon>
          <PushPin fontSize="small" />
        </ListItemIcon>
        <ListItemText>{selectedChat?.pinned ? "Unpin" : "Pin"}</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleShareChat}>
        <ListItemIcon>
          <Share fontSize="small" />
        </ListItemIcon>
        <ListItemText>Share</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleDeleteChat} sx={{ color: "error.main" }}>
        <ListItemIcon>
          <Delete fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );

  // Edit Message Dialog
  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Message</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={4}
          value={editMessageContent}
          onChange={(e) => setEditMessageContent(e.target.value)}
          variant="outlined"
          sx={{ mt: 1 }}
          helperText={`${editMessageContent.trim().split(/\s+/).length} / ${getChatWordLimit()} words`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSaveEdit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderRenameDialog = () => (
    <Dialog
      open={renameDialogOpen}
      onClose={() => setRenameDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Rename Chat</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          value={newChatTitle}
          onChange={(e) => setNewChatTitle(e.target.value)}
          variant="outlined"
          sx={{ mt: 1 }}
          placeholder="Enter new chat title..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSaveRename} variant="contained" disabled={!newChatTitle.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <AIIntelligenceBackground>
      <SubscriptionRequired open={subReqOpen} setOpen={setSubReqOpen} />
      
      {/* Coming Soon Popup */}
      <ComingSoonPopup open={comingSoonOpen} onClose={() => setComingSoonOpen(false)} />
      
      <Box sx={{ minHeight: "90vh", p: 2, position: 'relative', zIndex: 1 }}>
        <ScreenInfoOverlay activeTab={activeTab} />
        
        <div
          style={{
            height: "10vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "20px"
          }}
        >
          <h2
            style={{
              color: "#012a83",
              fontSize: "15px",
              fontWeight: "100",
              fontStyle: "bold",
              letterSpacing: "2px",
              margin: 0,
            }}
          >
            AI INTELLIGENCE HUB
          </h2>
        </div>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              fullWidth
              variant={activeTab === "generator" ? "contained" : "outlined"}
              startIcon={<ImageIcon />}
              onClick={handleGenArtStudioClick}
              sx={{
                py: 1,
                bgcolor: activeTab === "generator" ? "#3B82F6" : "white",
                color: activeTab === "generator" ? "white" : "#3B82F6",
                borderColor: "#3B82F6",
                "&:hover": {
                  bgcolor: activeTab === "generator" ? "#2563EB" : "rgba(59, 130, 246, 0.04)",
                },
              }}
            >
              AI GenArt Studio
            </Button>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              fullWidth
              variant={activeTab === "chatbot" ? "contained" : "outlined"}
              startIcon={<ChatIcon />}
              onClick={() => {
                if (!accessToAISmartDesk) {
                  toast.warn("AI SmartDesk is not available in your plan");
                  return;
                }
                setActiveTab("chatbot");
              }}
              sx={{
                py: 1,
                bgcolor: activeTab === "chatbot" ? "#8B5CF6" : "white",
                color: activeTab === "chatbot" ? "white" : "#8B5CF6",
                borderColor: "#8B5CF6",
                "&:hover": {
                  bgcolor: activeTab === "chatbot" ? "#7C3AED" : "rgba(139, 92, 246, 0.04)",
                },
              }}
              disabled={!accessToAISmartDesk}
            >
              AI SmartDesk
              {!accessToAISmartDesk && " (Premium)"}
            </Button>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              fullWidth
              variant={activeTab === "humanizer" ? "contained" : "outlined"}
              startIcon={<PersonIcon />}
              onClick={() => {
                if (!accessToHumanizer) {
                  toast.warn("Humanize ToneCraft is not available in your plan");
                  return;
                }
                setActiveTab("humanizer");
              }}
              sx={{
                py: 1,
                bgcolor: activeTab === "humanizer" ? "#10B981" : "white",
                color: activeTab === "humanizer" ? "white" : "#10B981",
                borderColor: "#10B981",
                "&:hover": {
                  bgcolor: activeTab === "humanizer" ? "#059669" : "rgba(16, 185, 129, 0.04)",
                },
              }}
              disabled={!accessToHumanizer}
            >
              Humanize ToneCraft
              {!accessToHumanizer && " (Premium)"}
            </Button>
          </Grid>
        </Grid>
        
        {activeTab === "generator" && renderImageGenerator()}
        {activeTab === "chatbot" && renderChatbot()}
        {activeTab === "humanizer" && (
          <HumanizerTab
            humanizerLevel={humanizerLevel}
            setHumanizerLevel={setHumanizerLevel}
            userID={userID}
            accessToLight={accessToLightLevel}
            accessToBalanced={accessToBalancedLevel}
            accessToMaximum={accessToMaximumLevel}
            userPlan={userPlan}
          />
        )}
      </Box>
      
      {renderMessageMenu()}
      {renderChatMenu()}
      {renderEditDialog()}
      {renderRenameDialog()}
      
      {/* Loading Backdrop */}
      <Backdrop open={isLoading} sx={{ color: "#fff", zIndex: 9999 }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Processing your request...</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            AI is working its magic
          </Typography>
        </Box>
      </Backdrop>
      
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {successMessage || "Action completed successfully!"}
        </Alert>
      </Snackbar>
      
      <ToastContainer />
    </AIIntelligenceBackground>
  );
}